/**
 * Receipt viewer — a standalone modal (mounts into #pickerRoot, not #modalRoot,
 * so it can layer over other modals like an edit form).
 *
 * Accepts either a legacy data: URL or a Storage path. For paths, fetches a
 * short-lived signed URL from the receipts bucket and uses that to render.
 */
import { escapeHtml, toast } from "../lib/dom";
import { isStoragePath, getReceiptSignedUrl } from "../lib/storage";

export async function viewReceipt(
  fileRef: string | null | undefined,
  mimeType?: string | null,
  fileName?: string | null,
): Promise<void> {
  if (!fileRef) { toast("No receipt attached", "err"); return; }

  let url = fileRef;
  if (isStoragePath(fileRef)) {
    try { url = await getReceiptSignedUrl(fileRef); }
    catch (e: any) { toast("Couldn't load receipt: " + (e?.message || e), "err"); return; }
  }

  let root = document.getElementById("pickerRoot");
  if (!root) { root = document.createElement("div"); root.id = "pickerRoot"; document.body.appendChild(root); }

  const lowerName = (fileName || "").toLowerCase();
  const isImg = (mimeType && mimeType.startsWith("image/"))
    || /\.(png|jpe?g|gif|webp|heic|bmp)$/i.test(lowerName)
    || url.startsWith("data:image/");
  const isPdf = mimeType === "application/pdf"
    || /\.pdf$/i.test(lowerName)
    || url.startsWith("data:application/pdf");

  let body: string;
  if (isImg) {
    body = `<div style="text-align:center"><img src="${url}" style="max-width:100%; max-height:75vh; border-radius:8px;" alt="Receipt"></div>`;
  } else if (isPdf) {
    body = `<embed src="${url}" type="application/pdf" style="width:100%; height:75vh; border-radius:8px;">`;
  } else {
    body = `<div class="text-center text-[var(--muted)] p-4">This file type can't be previewed inline. Use Download below.</div>`;
  }

  const dlName = fileName || "receipt";
  root.innerHTML = `
    <div class="fixed inset-0 modal-backdrop flex items-start justify-center p-3 sm:p-6 overflow-y-auto" style="z-index:55">
      <div class="panel w-full max-w-5xl my-6 shadow-2xl">
        <div class="flex items-center justify-between p-4 border-b border-[var(--border)]">
          <h3 class="font-semibold truncate">Receipt${fileName ? ` — ${escapeHtml(fileName)}` : ""}</h3>
          <button class="btn btn-ghost !py-1 !px-2 shrink-0" id="_recvClose" title="Close">✕</button>
        </div>
        <div class="p-4">${body}</div>
        <div class="p-4 border-t border-[var(--border)] flex justify-end gap-2">
          <a class="btn btn-ghost" href="${url}" download="${escapeHtml(dlName)}" target="_blank" rel="noopener">⤓ Download</a>
          <button class="btn btn-ghost" id="_recvCloseBtn">Close</button>
        </div>
      </div>
    </div>`;
  document.getElementById("_recvClose")?.addEventListener("click", closeReceiptViewer);
  document.getElementById("_recvCloseBtn")?.addEventListener("click", closeReceiptViewer);
}

export function closeReceiptViewer(): void {
  const root = document.getElementById("pickerRoot");
  if (root) root.innerHTML = "";
}
